/**
 * Pure helpers for the save() pipeline of usePendingChannelDrops.
 *
 * `groupShiftsByChannel` groups the flat shift list by channel for the
 * per-channel reorder loop. `buildLinkIdsForChannel` derives the link-id list
 * in NEW slot order from a snapshot + the channel's shifts, ready to be sent
 * to `reorderProductImages` (which compacts gaps server-side).
 *
 * Rules (see Bundle-D contract):
 * - A shift `{from, to}` means the link currently at slot `from` should land
 *   at slot `to` after the reorder.
 * - Slots that are not in any shift stay where they are.
 * - Pending inserts are NOT in the resulting link-id list — the inserted asset
 *   is materialised AFTER the reorder by transfer+link (step 3 of the pipe).
 */
import type { AddToProductImage } from '../types'

export interface PendingShift {
  channelId: string
  from: number
  to: number
  linkId?: string
}

export type ChannelSnapshot = Record<
  number,
  { linkId: string; image: AddToProductImage }
>

export function groupShiftsByChannel(
  shifts: PendingShift[],
): Map<string, PendingShift[]> {
  const out = new Map<string, PendingShift[]>()
  for (const s of shifts) {
    const list = out.get(s.channelId) ?? []
    list.push(s)
    out.set(s.channelId, list)
  }
  return out
}

/**
 * Returns link-ids in the NEW slot order. Empty array → no reorder needed.
 * Algorithm:
 *  1. For every occupied slot in the snapshot, look up its shift target
 *     (`{from: slot}` → use shift.to; otherwise stay at slot).
 *  2. Sort link-ids by their target slot ascending.
 */
export function buildLinkIdsForChannel(
  _channelId: string,
  shifts: PendingShift[],
  snapshot: ChannelSnapshot,
): string[] {
  if (shifts.length === 0) return []
  const byFrom = new Map(shifts.map((s) => [s.from, s.to]))
  const entries = Object.entries(snapshot)
    .map(([slotStr, v]) => ({
      linkId: v.linkId,
      target: byFrom.get(Number(slotStr)) ?? Number(slotStr),
    }))
    .sort((a, b) => a.target - b.target)
  return entries.map((e) => e.linkId)
}

/** One insert that was appended at a TEMP slot but should end up at targetSlot. */
export interface ChannelInsert {
  /** The slot the user dropped the asset onto (= final target slot). */
  targetSlot: number
  /** The link_id created by linkAssetToChannel for this insert. */
  linkId: string
}

/**
 * Returns the FULL link-id list in final slot order for a channel after both
 * `shifts` (existing items moving) and `inserts` (newly linked items) are
 * applied. Returns `[]` when no reorder is needed (no shifts AND every insert
 * already sits at its target slot via append).
 *
 * Used by `runChannelReorderFinal` to compute the argument for
 * `reorderProductImages`. The backend stamps the supplied link-ids on slots
 * 1..N in input order, so this list must be exhaustive and ordered.
 */
export function buildFinalLinkIds(
  shifts: PendingShift[],
  snapshot: ChannelSnapshot,
  inserts: ChannelInsert[],
  maxExistingSlot: number,
): string[] {
  if (shifts.length === 0 && inserts.length === 0) return []
  const byFrom = new Map(shifts.map((s) => [s.from, s.to]))
  const placed = new Map<number, string>()
  for (const [slotStr, v] of Object.entries(snapshot)) {
    const fromSlot = Number(slotStr)
    const target = byFrom.get(fromSlot) ?? fromSlot
    placed.set(target, v.linkId)
  }
  for (const ins of inserts) placed.set(ins.targetSlot, ins.linkId)
  // No reorder needed when no shifts AND every insert already landed at its
  // target slot during the append step (temp == targetSlot).
  if (shifts.length === 0) {
    const allInPlace = inserts.every(
      (ins, i) => ins.targetSlot === maxExistingSlot + 1 + i,
    )
    if (allInPlace) return []
  }
  return Array.from(placed.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, linkId]) => linkId)
}
